import { QueryClient, QueryClientProvider, useMutation, useQuery } from '@tanstack/react-query'
import {
  Activity,
  Box,
  Boxes,
  CircleAlert,
  Clipboard,
  Clock3,
  Copy,
  Database,
  HardDrive,
  KeyRound,
  Layers3,
  LogOut,
  Play,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  Users,
} from 'lucide-react'
import { StrictMode, useEffect, useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { authProvider, type AuthenticatedUser } from './auth'
import {
  api,
  apiRequest,
  type ClusterConfig,
  type CreateSandboxResult,
  type DiskCacheMetrics,
  type Overview,
  type OrchestratorNode,
  type Orchestrators,
  type ProvisionTeamResult,
  type Team,
  type TeamDetail,
  type Template,
} from './api'
import './styles.css'

const queryClient = new QueryClient()
const clusterLabel = import.meta.env.VITE_CLUSTER_LABEL || 'Sandbox cluster'
const adminEmailHint = import.meta.env.VITE_ADMIN_EMAIL_HINT || 'admin email'
type DashboardView = 'overview' | 'teams' | 'orchestrators' | 'templates' | 'snapshots'

function useAuthUser() {
  const [user, setUser] = useState<AuthenticatedUser | null | undefined>(undefined)
  useMemo(() => authProvider.subscribe(setUser), [])
  return user
}

function Root() {
  const user = useAuthUser()
  const [linkError, setLinkError] = useState<string | null>(null)

  useMemo(() => {
    if (!authProvider.isConfigured || !authProvider.hasSignInLink()) return
    authProvider.finishSignInLink().catch((error) => {
      setLinkError(error instanceof Error ? error.message : 'Email sign-in failed')
    })
  }, [])

  if (!authProvider.isConfigured) return <ConfigMissing />
  if (user === undefined) return <ShellLoading />
  if (!user) return <Login linkError={linkError} />

  return (
    <QueryClientProvider client={queryClient}>
      <Dashboard user={user} />
    </QueryClientProvider>
  )
}

function ConfigMissing() {
  return (
    <main className="auth-screen">
      <section className="auth-panel">
        <CircleAlert size={28} />
        <h1>Sandbox Control Plane</h1>
        <p>Auth provider config is missing.</p>
      </section>
    </main>
  )
}

function ShellLoading() {
  return (
    <main className="auth-screen">
      <section className="auth-panel">
        <RefreshCw className="spin" size={28} />
        <h1>Sandbox Control Plane</h1>
        <p>Loading session...</p>
      </section>
    </main>
  )
}

function Login({ linkError }: { linkError: string | null }) {
  const [credential, setCredential] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(linkError)
  const isTokenAuth = authProvider.mode === 'token'

  async function submitCredential(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    const normalized = credential.trim()
    if (!normalized) {
      setError(isTokenAuth ? 'Token is required.' : 'Email is required.')
      return
    }
    if (!isTokenAuth && !normalized.includes('@')) {
      setError('Enter a valid email address.')
      return
    }
    try {
      await authProvider.authenticate(isTokenAuth ? normalized : normalized.toLowerCase())
      setSent(!isTokenAuth)
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'Authentication failed')
    }
  }

  return (
    <main className="auth-screen">
      <section className="auth-panel">
        <ShieldCheck size={32} />
        <h1>Sandbox Control Plane</h1>
        <p>
          {isTokenAuth
            ? 'Cluster operations require an admin token.'
            : 'Cluster operations require an authorized admin email.'}
        </p>
        <form className="email-form" onSubmit={(event) => void submitCredential(event)}>
          <input
            value={credential}
            onChange={(event) => setCredential(event.target.value)}
            placeholder={isTokenAuth ? 'Admin token' : adminEmailHint}
            type={isTokenAuth ? 'password' : 'email'}
          />
          <button className="primary-action" type="submit">
            <KeyRound size={18} />
            {isTokenAuth ? 'Sign in' : 'Send sign-in link'}
          </button>
        </form>
        {sent && <p className="auth-note">Check your email for the sign-in link.</p>}
        {error && <p className="auth-error">{error}</p>}
      </section>
    </main>
  )
}

function Dashboard({ user }: { user: AuthenticatedUser }) {
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [activeView, setActiveView] = useState<DashboardView>('overview')

  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => api<Overview>(user, '/api/overview'),
    refetchInterval: 20_000,
  })

  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<Team[]>(user, '/api/teams'),
    refetchInterval: 20_000,
  })

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<Template[]>(user, '/api/templates'),
    refetchInterval: 60_000,
  })

  const orchestrators = useQuery({
    queryKey: ['orchestrators'],
    queryFn: () => api<Orchestrators>(user, '/api/orchestrators'),
    refetchInterval: 20_000,
  })

  const visibleTeams = (teams.data ?? []).filter((team) =>
    team.team_id.toLowerCase().includes(filter.toLowerCase()),
  )
  const activeTeam = selectedTeam ?? visibleTeams[0]?.team_id ?? null
  const viewCopy = {
    overview: ['Runtime control plane', 'Cluster credentials, provisioning, and recent activity'],
    teams: ['Teams', 'Team records, API keys, sandboxes, snapshots, and volumes'],
    orchestrators: ['Orchestrators', 'Node health, local cache, and NFS cache capacity'],
    templates: ['Templates', 'Storage-backed runtime template catalog'],
    snapshots: ['Snapshots', 'Recent sandbox and snapshot activity'],
  } satisfies Record<DashboardView, [string, string]>
  const pageError =
    activeView === 'templates'
      ? templates.error
      : activeView === 'orchestrators'
        ? orchestrators.error
        : activeView === 'teams'
          ? teams.error
          : overview.error || teams.error

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <Box size={20} />
          </div>
          <div>
            <strong>Sandbox</strong>
            <span>Control Plane</span>
          </div>
        </div>

        <nav className="nav">
          <button
            className={activeView === 'overview' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('overview')}
            type="button"
          >
            <Activity size={17} />
            Overview
          </button>
          <button
            className={activeView === 'teams' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('teams')}
            type="button"
          >
            <Users size={17} />
            Teams
          </button>
          <button
            className={activeView === 'orchestrators' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('orchestrators')}
            type="button"
          >
            <Server size={17} />
            Orchestrators
          </button>
          <button
            className={activeView === 'templates' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('templates')}
            type="button"
          >
            <Database size={17} />
            Templates
          </button>
          <button
            className={activeView === 'snapshots' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('snapshots')}
            type="button"
          >
            <Layers3 size={17} />
            Snapshots
          </button>
        </nav>

        <div className="user-card">
          {user.photoURL ? <img src={user.photoURL} alt="" /> : <div className="avatar-fallback" />}
          <div>
            <strong>{user.displayName ?? 'Admin'}</strong>
            <span>{user.email}</span>
          </div>
          <button className="icon-button" onClick={() => void authProvider.logout()} aria-label="Sign out">
            <LogOut size={17} />
          </button>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div>
            <p>{clusterLabel}</p>
            <h1>{viewCopy[activeView][0]}</h1>
            <span>{viewCopy[activeView][1]}</span>
          </div>
          <button
            className="secondary-action"
            onClick={() => {
              void overview.refetch()
              void teams.refetch()
              void templates.refetch()
              void orchestrators.refetch()
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </header>

        {pageError ? (
          <ErrorState error={pageError} />
        ) : (
          <>
            {activeView === 'overview' && (
              <>
                <section className="metric-grid">
                  <Metric icon={<Users />} label="Teams" value={overview.data?.summary.teams} />
                  <Metric icon={<KeyRound />} label="API keys" value={overview.data?.summary.api_keys} />
                  <Metric icon={<Boxes />} label="Sandboxes" value={overview.data?.summary.sandboxes} />
                  <Metric
                    icon={<Activity />}
                    label="Running"
                    value={overview.data?.summary.running_sandboxes}
                  />
                  <Metric
                    icon={<Clock3 />}
                    label="Paused"
                    value={overview.data?.summary.paused_sandboxes}
                  />
                  <Metric icon={<Layers3 />} label="Snapshots" value={overview.data?.summary.snapshots} />
                  <Metric icon={<Database />} label="Volumes" value={overview.data?.summary.volumes} />
                </section>

                <AdminProvisioning
                  user={user}
                  templates={templates.data ?? []}
                  onChanged={() => {
                    void overview.refetch()
                    void teams.refetch()
                  }}
                />

                <section className="bottom-grid">
                  <ActivityPanel title="Recent sandboxes" rows={overview.data?.recentSandboxes ?? []} />
                  <SnapshotPanel title="Recent snapshots" rows={overview.data?.recentSnapshots ?? []} />
                </section>
              </>
            )}

            {activeView === 'teams' && (
              <section className="content-grid">
                <div className="panel team-panel">
                  <div className="panel-header">
                    <div>
                      <h2>Teams</h2>
                      <p>{visibleTeams.length} visible</p>
                    </div>
                    <label className="search">
                      <Search size={16} />
                      <input
                        value={filter}
                        onChange={(event) => setFilter(event.target.value)}
                        placeholder="Filter team"
                      />
                    </label>
                  </div>
                  <div className="team-list">
                    {visibleTeams.map((team) => (
                      <button
                        key={team.team_id}
                        className={team.team_id === activeTeam ? 'team-row selected' : 'team-row'}
                        onClick={() => setSelectedTeam(team.team_id)}
                      >
                        <span className="team-id">{team.team_id}</span>
                        <span>{team.api_keys} keys</span>
                        <span>{team.running} running</span>
                        <span>{team.snapshots} snapshots</span>
                      </button>
                    ))}
                  </div>
                </div>

                <TeamInspector user={user} teamId={activeTeam} />
              </section>
            )}

            {activeView === 'orchestrators' && (
              <OrchestratorPanel
                data={orchestrators.data}
                isLoading={orchestrators.isLoading}
                isRefreshing={orchestrators.isFetching}
                onRefresh={() => void orchestrators.refetch()}
              />
            )}

            {activeView === 'templates' && (
              <TemplateCatalog
                isLoading={templates.isLoading}
                isRefreshing={templates.isFetching}
                onRefresh={() => void templates.refetch()}
                templates={templates.data ?? []}
              />
            )}

            {activeView === 'snapshots' && (
              <section className="bottom-grid">
                <ActivityPanel title="Recent sandboxes" rows={overview.data?.recentSandboxes ?? []} />
                <SnapshotPanel title="Recent snapshots" rows={overview.data?.recentSnapshots ?? []} />
              </section>
            )}
          </>
        )}
      </main>
    </div>
  )
}

function AdminProvisioning({
  user,
  templates,
  onChanged,
}: {
  user: AuthenticatedUser
  templates: Template[]
  onChanged: () => void
}) {
  const defaultTemplate = templates.find((template) => template.default) ?? templates[0]
  const [displayName, setDisplayName] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState(defaultTemplate?.id ?? '')
  const [timeout, setTimeoutValue] = useState(300)
  const [copied, setCopied] = useState(false)
  const [masterCopied, setMasterCopied] = useState(false)
  const [showMasterKey, setShowMasterKey] = useState(false)

  const clusterConfig = useQuery({
    queryKey: ['cluster-config'],
    queryFn: () => api<ClusterConfig>(user, '/api/cluster-config'),
  })

  const gatewayUrl = clusterConfig.data?.sandboxGatewayUrl ?? 'https://xyznot.com'
  const masterKey = clusterConfig.data?.sandboxMasterKey ?? ''
  const masterKeyDisplay = masterKey
    ? showMasterKey
      ? masterKey
      : clusterConfig.data?.sandboxMasterKeyMask ?? '<configured>'
    : '<not configured>'

  useEffect(() => {
    if (!selectedTemplate && defaultTemplate) {
      setSelectedTemplate(defaultTemplate.id)
    }
  }, [defaultTemplate, selectedTemplate])

  const provision = useMutation({
    mutationFn: () =>
      apiRequest<ProvisionTeamResult>(user, '/api/teams', {
        method: 'POST',
        body: JSON.stringify({ displayName }),
      }),
    onSuccess: () => {
      onChanged()
    },
  })

  const generatedApiKey = provision.data?.apiKeySecret ?? ''

  const sandbox = useMutation({
    mutationFn: () =>
      apiRequest<CreateSandboxResult>(user, '/api/sandboxes', {
        method: 'POST',
        body: JSON.stringify({ apiKey: generatedApiKey, templateID: selectedTemplate, timeout }),
      }),
    onSuccess: () => {
      onChanged()
    },
  })

  const curl = `curl -X POST ${gatewayUrl}/sandboxes \\
  -H "Authorization: Bearer ${generatedApiKey || '<sandbox_api_key>'}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify({ templateID: selectedTemplate || '<template_id>', timeout })}'`
  const envSnippet = `export SANDBOX_API_URL=${gatewayUrl}
export SANDBOX_API_KEY=${generatedApiKey || '<sandbox_api_key>'}
export SANDBOX_TEMPLATE_ID=${selectedTemplate || '<template_id>'}`

  async function copyKey() {
    if (!generatedApiKey) return
    await navigator.clipboard.writeText(generatedApiKey)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1600)
  }

  async function copyMasterKey() {
    if (!masterKey) return
    await navigator.clipboard.writeText(masterKey)
    setMasterCopied(true)
    window.setTimeout(() => setMasterCopied(false), 1600)
  }

  return (
    <section className="provision-grid">
      <div className="panel provision-panel">
        <div className="panel-header">
          <div>
            <h2>Cluster credentials</h2>
            <p>Admin-only Sandbox control-plane settings</p>
          </div>
        </div>
        <div className="setup-box">
          <div className="secret-box">
            <div>
              <span>Gateway URL</span>
              <code>{gatewayUrl}</code>
              <span>Sandbox master key</span>
              <code>{clusterConfig.isLoading ? 'Loading...' : masterKeyDisplay}</code>
            </div>
            <div className="secret-actions">
              <button
                className="secondary-action"
                disabled={!masterKey}
                onClick={() => setShowMasterKey((value) => !value)}
              >
                {showMasterKey ? 'Hide' : 'Reveal'}
              </button>
              <button className="secondary-action" disabled={!masterKey} onClick={() => void copyMasterKey()}>
                <Copy size={15} />
                {masterCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          {clusterConfig.error && <p className="inline-error">{errorText(clusterConfig.error)}</p>}
        </div>
      </div>

      <div className="panel provision-panel">
        <div className="panel-header">
          <div>
            <h2>Provision team</h2>
            <p>Create a team-scoped Sandbox API key</p>
          </div>
        </div>

        <form
          className="provision-form"
          onSubmit={(event) => {
            event.preventDefault()
            provision.mutate()
          }}
        >
          <label>
            <span>Team name</span>
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Acme Sandbox"
            />
          </label>
          <button className="primary-action" disabled={provision.isPending} type="submit">
            <KeyRound size={17} />
            {provision.isPending ? 'Creating...' : 'Create team key'}
          </button>
        </form>

        {provision.error && <p className="inline-error">{errorText(provision.error)}</p>}
        {provision.data && (
          <div className="setup-box">
            <div className="secret-box">
              <div>
                <span>Team ID</span>
                <code>{provision.data.teamId}</code>
                <span>API key secret</span>
                <code>{provision.data.apiKeySecret}</code>
              </div>
              <button className="secondary-action" onClick={() => void copyKey()}>
                <Copy size={15} />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="env-box">
              <code>{envSnippet}</code>
            </pre>
          </div>
        )}
      </div>

      <div className="panel template-panel">
        <div className="panel-header">
          <div>
            <h2>Templates</h2>
            <p>Fixed catalog for this cluster</p>
          </div>
        </div>

        <div className="template-list">
          {templates.map((template) => (
            <button
              key={template.id}
              className={template.id === selectedTemplate ? 'template-row selected' : 'template-row'}
              onClick={() => setSelectedTemplate(template.id)}
            >
              <span>{template.name}</span>
              <strong>{template.id}</strong>
              <em>{templateDetails(template)}</em>
            </button>
          ))}
        </div>

        <div className="sandbox-create">
          <label>
            <span>Timeout seconds</span>
            <input
              value={timeout}
              min={30}
              max={86400}
              onChange={(event) => setTimeoutValue(Number(event.target.value))}
              type="number"
            />
          </label>
          <button
            className="secondary-action"
            disabled={!generatedApiKey || !selectedTemplate || sandbox.isPending}
            onClick={() => sandbox.mutate()}
          >
            <Play size={15} />
            {sandbox.isPending ? 'Starting...' : 'Create sandbox'}
          </button>
        </div>

        <pre className="curl-box">
          <Clipboard size={15} />
          <code>{curl}</code>
        </pre>

        {sandbox.error && <p className="inline-error">{errorText(sandbox.error)}</p>}
        {sandbox.data && (
          <p className="inline-success">
            Created {sandbox.data.sandboxID} on {sandbox.data.domain}
          </p>
        )}
      </div>
    </section>
  )
}

function OrchestratorPanel({
  data,
  isLoading,
  isRefreshing,
  onRefresh,
}: {
  data?: Orchestrators
  isLoading: boolean
  isRefreshing: boolean
  onRefresh: () => void
}) {
  const nodes = data?.orchestrators ?? []
  const totalSandboxes = nodes.reduce((sum, node) => sum + (node.metrics?.sandboxCount ?? node.status?.totalVms ?? 0), 0)
  const localAvailableGb = sumCacheAvailable(nodes, 'localCache')
  const nfsAvailableGb = sumCacheAvailable(nodes, 'nfsCache')

  return (
    <>
      <section className="metric-grid orchestrator-metrics">
        <Metric
          icon={<Server />}
          label="Healthy"
          value={data ? `${data.healthyCount}/${data.totalCount}` : '-'}
        />
        <Metric icon={<Boxes />} label="Sandboxes" value={totalSandboxes} />
        <Metric icon={<HardDrive />} label="/mnt/storage free" value={formatGb(localAvailableGb)} />
        <Metric icon={<Database />} label="/mnt/nfs-cache free" value={formatGb(nfsAvailableGb)} />
      </section>

      <section className="panel catalog-panel">
        <div className="panel-header">
          <div>
            <h2>Orchestrator Nodes</h2>
            <p>
              {data
                ? `${data.gatewayUrl} /health returned ${data.gatewayStatus} (${data.gatewayHttpStatus})`
                : 'Gateway health and node cache metrics'}
            </p>
          </div>
          <button className="secondary-action" disabled={isRefreshing} onClick={onRefresh} type="button">
            <RefreshCw className={isRefreshing ? 'spin' : undefined} size={16} />
            Refresh
          </button>
        </div>

        <div className="table-wrap catalog-table-wrap">
          <table className="orchestrator-table">
            <thead>
              <tr>
                <th>Node</th>
                <th>Health</th>
                <th>VMs</th>
                <th>CPU</th>
                <th>Memory</th>
                <th>Local cache</th>
                <th>NFS cache</th>
                <th>Build</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8}>Loading orchestrators...</td>
                </tr>
              )}
              {!isLoading && nodes.length === 0 && (
                <tr>
                  <td colSpan={8}>No orchestrators reported by gateway health</td>
                </tr>
              )}
              {!isLoading &&
                nodes.map((node) => (
                  <tr key={node.id}>
                    <td>
                      <div className="node-cell">
                        <strong>{node.id}</strong>
                        <code>{node.address || '-'}</code>
                      </div>
                    </td>
                    <td>
                      <div className="node-health-cell">
                        <Status status={node.healthy ? 'healthy' : 'unhealthy'} />
                        {node.error && <span>{node.error}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="template-runtime-cell">
                        <span>{node.metrics?.sandboxCount ?? node.status?.totalVms ?? '-'} sandboxes</span>
                        <span>{node.status?.fcProcessCount ?? '-'} firecracker</span>
                      </div>
                    </td>
                    <td>
                      <div className="template-runtime-cell">
                        <span>
                          {node.metrics?.cpuAllocated ?? '-'} / {node.metrics?.cpuCount ?? '-'} vCPU
                        </span>
                        <span>{formatPercent(node.metrics?.cpuPercent)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="template-runtime-cell">
                        <span>{formatGb(node.metrics?.memoryAvailableGb)} free</span>
                        <span>{formatGb(node.metrics?.memoryTotalGb)} total</span>
                      </div>
                    </td>
                    <td>
                      <CacheCell cache={node.metrics?.localCache ?? null} fallbackPath="/mnt/storage" />
                    </td>
                    <td>
                      <CacheCell cache={node.metrics?.nfsCache ?? null} fallbackPath="/mnt/nfs-cache" />
                    </td>
                    <td>
                      <div className="template-runtime-cell">
                        <span>{node.metrics?.commit ?? '-'}</span>
                        <span>{node.metrics?.uptime ?? '-'}</span>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  )
}

function CacheCell({ cache, fallbackPath }: { cache: DiskCacheMetrics | null; fallbackPath: string }) {
  if (!cache) {
    return (
      <div className="cache-cell">
        <div>
          <strong>{fallbackPath}</strong>
          <Status status="unknown" />
        </div>
        <span>No metrics</span>
      </div>
    )
  }

  const status = cacheStatus(cache)
  return (
    <div className="cache-cell">
      <div>
        <strong>{cache.path}</strong>
        <Status status={status} />
      </div>
      <span>{cacheCapacity(cache)}</span>
      {cache.usedPercent !== null && (
        <div className="usage-bar" aria-label={`${cache.path} usage ${formatPercent(cache.usedPercent)}`}>
          <span style={{ width: `${Math.max(1, Math.min(cache.usedPercent, 100))}%` }} />
        </div>
      )}
    </div>
  )
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value?: number | string }) {
  return (
    <div className="metric">
      <div className="metric-icon">{icon}</div>
      <span>{label}</span>
      <strong>{value ?? '-'}</strong>
    </div>
  )
}

function sumCacheAvailable(nodes: OrchestratorNode[], key: 'localCache' | 'nfsCache') {
  return nodes.reduce((sum, node) => sum + (node.metrics?.[key].availableGb ?? 0), 0)
}

function cacheStatus(cache: DiskCacheMetrics) {
  if (!cache.mounted) return 'missing'
  if (cache.unlimited) return 'unlimited'
  if (cache.usedPercent !== null && cache.usedPercent >= 95) return 'full'
  if (cache.usedPercent !== null && cache.usedPercent >= 85) return 'low'
  return 'mounted'
}

function cacheCapacity(cache: DiskCacheMetrics) {
  if (!cache.mounted) return 'missing'
  if (cache.unlimited) return `${formatGb(cache.availableGb)} free / unlimited`
  return `${formatGb(cache.availableGb)} free / ${formatGb(cache.totalGb)} total`
}

function TemplateCatalog({
  templates,
  isLoading,
  isRefreshing,
  onRefresh,
}: {
  templates: Template[]
  isLoading: boolean
  isRefreshing: boolean
  onRefresh: () => void
}) {
  const [filter, setFilter] = useState('')
  const visibleTemplates = templates.filter((template) =>
    templateSearchText(template).toLowerCase().includes(filter.toLowerCase()),
  )
  const available = templates.filter((template) => templateStatus(template) === 'available').length
  const totalBytes = templates.reduce((sum, template) => sum + (template.artifactSizeBytes ?? 0), 0)
  const newestCreatedAt = templates
    .map((template) => template.createdAt)
    .filter(Boolean)
    .sort()
    .at(-1)

  return (
    <>
      <section className="metric-grid template-metrics">
        <Metric icon={<Database />} label="Templates" value={templates.length} />
        <Metric icon={<Activity />} label="Available" value={available} />
        <Metric icon={<HardDrive />} label="Stored" value={totalBytes ? formatBytes(totalBytes) : '-'} />
        <Metric icon={<Clock3 />} label="Newest" value={formatTime(newestCreatedAt)} />
      </section>

      <section className="panel catalog-panel">
        <div className="panel-header">
          <div>
            <h2>Template Catalog</h2>
            <p>{templateCatalogUri(templates)}</p>
          </div>
          <div className="catalog-actions">
            <label className="search">
              <Search size={16} />
              <input
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder="Filter templates"
              />
            </label>
            <button className="secondary-action" disabled={isRefreshing} onClick={onRefresh} type="button">
              <RefreshCw className={isRefreshing ? 'spin' : undefined} size={16} />
              Refresh
            </button>
          </div>
        </div>

        <div className="table-wrap catalog-table-wrap">
          <table className="template-catalog-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Status</th>
                <th>Source</th>
                <th>Memory</th>
                <th>Disk</th>
                <th>Stored</th>
                <th>Created</th>
                <th>Runtime</th>
                <th>Storage</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={9}>Loading templates...</td>
                </tr>
              )}
              {!isLoading && visibleTemplates.length === 0 && (
                <tr>
                  <td colSpan={9}>No templates found</td>
                </tr>
              )}
              {!isLoading &&
                visibleTemplates.map((template) => (
                  <tr key={template.id}>
                    <td>
                      <div className="template-cell">
                        <strong>{template.name}</strong>
                        <code>{template.id}</code>
                        {template.default && <span>default</span>}
                      </div>
                    </td>
                    <td>
                      <Status status={templateStatus(template)} />
                    </td>
                    <td>{templateSource(template)}</td>
                    <td>{template.memoryMb ? formatMb(template.memoryMb) : '-'}</td>
                    <td>{template.rootfsSizeMb ? formatMb(template.rootfsSizeMb) : '-'}</td>
                    <td>{template.artifactSizeBytes ? formatBytes(template.artifactSizeBytes) : '-'}</td>
                    <td>{formatTime(template.createdAt)}</td>
                    <td>
                      <div className="template-runtime-cell">
                        <span>{templateVcpu(template)}</span>
                        <span>{templateKernel(template)}</span>
                      </div>
                    </td>
                    <td title={template.storageUri ?? template.bucket}>
                      <code className="storage-uri">{template.storageUri ?? template.bucket ?? '-'}</code>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  )
}

function templateDetails(template: Template) {
  const parts = []
  if (template.status) parts.push(template.status)
  if (template.memoryMb) parts.push(`${formatMb(template.memoryMb)} memory`)
  if (template.rootfsSizeMb) parts.push(`${formatMb(template.rootfsSizeMb)} disk`)
  if (template.artifactSizeBytes) parts.push(`${formatBytes(template.artifactSizeBytes)} stored`)
  if (template.createdAt) parts.push(`created ${formatTime(template.createdAt)}`)
  return parts.join(' / ') || template.description
}

function templateStatus(template: Template) {
  return template.status || 'available'
}

function templateSearchText(template: Template) {
  return [
    template.id,
    template.name,
    template.description,
    template.status,
    template.storageUri,
    template.bucket,
    templateSource(template),
    templateKernel(template),
  ]
    .filter(Boolean)
    .join(' ')
}

function templateCatalogUri(templates: Template[]) {
  const storageUri = templates.find((template) => template.storageUri)?.storageUri
  if (storageUri) return storageUri.replace(/[^/]+\/?$/, '')
  const bucket = templates.find((template) => template.bucket)?.bucket
  return bucket ? `gs://${bucket}/templates/` : 'Storage-backed runtime templates'
}

function templateSource(template: Template) {
  const metadata = template.metadata
  return stringMetadata(metadata, 'from_image') || stringMetadata(metadata, 'base_template_id') || '-'
}

function templateVcpu(template: Template) {
  const value = numberMetadata(template.metadata, 'vcpu_count')
  return value ? `${value} vCPU` : '-'
}

function templateKernel(template: Template) {
  const templateMetadata = objectMetadata(template.metadata, 'template')
  return stringMetadata(templateMetadata, 'kernel_version') || '-'
}

function objectMetadata(metadata: Record<string, unknown> | undefined, key: string) {
  const value = metadata?.[key]
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined
}

function stringMetadata(metadata: Record<string, unknown> | undefined, key: string) {
  const value = metadata?.[key]
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function numberMetadata(metadata: Record<string, unknown> | undefined, key: string) {
  const value = metadata?.[key]
  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : undefined
}

function formatGb(value?: number | null) {
  if (value === null || value === undefined) return '-'
  return `${trimNumber(value)} GB`
}

function formatPercent(value?: number | null) {
  if (value === null || value === undefined) return '-'
  return `${trimNumber(value)}%`
}

function formatMb(value: number) {
  return value >= 1024 ? `${trimNumber(value / 1024)} GB` : `${value} MB`
}

function formatBytes(value: number) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let size = value
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${trimNumber(size)} ${units[unit]}`
}

function trimNumber(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

function TeamInspector({ user, teamId }: { user: AuthenticatedUser; teamId: string | null }) {
  const detail = useQuery({
    queryKey: ['team', teamId],
    enabled: Boolean(teamId),
    queryFn: () => api<TeamDetail>(user, `/api/teams/${encodeURIComponent(teamId ?? '')}`),
    refetchInterval: 20_000,
  })

  if (!teamId) {
    return (
      <div className="panel inspector">
        <div className="empty-state">No teams found</div>
      </div>
    )
  }

  const data = detail.data
  return (
    <div className="panel inspector">
      <div className="panel-header">
        <div>
          <h2>{teamId}</h2>
          <p>Team runtime state</p>
        </div>
      </div>

      <div className="mini-tabs">
        <span>{data?.keys.length ?? '-'} keys</span>
        <span>{data?.sandboxes.length ?? '-'} sandboxes</span>
        <span>{data?.snapshots.length ?? '-'} snapshots</span>
        <span>{data?.volumes.length ?? '-'} volumes</span>
      </div>

      <div id="keys" className="table-wrap">
        <h3>API keys</h3>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Last used</th>
            </tr>
          </thead>
          <tbody>
            {(data?.keys ?? []).map((key) => (
              <tr key={key.id}>
                <td>{key.name}</td>
                <td>{key.api_key_mask ?? key.api_key_prefix}</td>
                <td>{formatTime(key.last_used_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-wrap">
        <h3>Sandboxes</h3>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {(data?.sandboxes ?? []).slice(0, 8).map((sandbox) => (
              <tr key={sandbox.id}>
                <td>{sandbox.id}</td>
                <td>
                  <Status status={sandbox.status} />
                </td>
                <td>{formatTime(sandbox.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ActivityPanel({
  title,
  rows,
}: {
  title: string
  rows: Overview['recentSandboxes']
}) {
  return (
    <div className="panel compact-panel">
      <div className="panel-header">
        <h2>{title}</h2>
      </div>
      <div className="event-list">
        {rows.map((row) => (
          <div className="event-row" key={row.id}>
            <div>
              <strong>{row.id}</strong>
              <span>{row.team_id}</span>
            </div>
            <Status status={row.status} />
          </div>
        ))}
      </div>
    </div>
  )
}

function SnapshotPanel({
  title,
  rows,
}: {
  title: string
  rows: Overview['recentSnapshots']
}) {
  return (
    <div id="snapshots" className="panel compact-panel">
      <div className="panel-header">
        <h2>{title}</h2>
      </div>
      <div className="event-list">
        {rows.map((row) => (
          <div className="event-row" key={row.build_id}>
            <div>
              <strong>{row.sandbox_id}</strong>
              <span>{formatTime(row.created_at)}</span>
            </div>
            <Status status={row.snapshot_state} />
          </div>
        ))}
      </div>
    </div>
  )
}

function Status({ status }: { status: string }) {
  return <span className={`status status-${status}`}>{status}</span>
}

function ErrorState({ error }: { error: unknown }) {
  return (
    <section className="panel error-panel">
      <CircleAlert size={22} />
      <div>
        <h2>Request failed</h2>
        <p>{error instanceof Error ? error.message : 'Unknown error'}</p>
      </div>
    </section>
  )
}

function formatTime(value?: string | null) {
  if (!value) return '-'
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function errorText(error: unknown) {
  return error instanceof Error ? error.message : 'Request failed'
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
)
